import { useState, useEffect } from "react";
import {
  Container,
  Dialog,
  Grid,
  TextField,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@material-ui/core";
import { IoIosClose } from "react-icons/io";
import { toast } from "react-toastify";

import { HydrovacTicket } from "../../interface/api/HydrovacTicket";
import { hydrovacTicketNumber } from "../../utils/parser";
import { ContainmentPad } from "../../interface/api/ContainmentPad";
import storageAreaService from "../../services/storageAreaService";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import contractorCompanyService from "../../services/contractorCompanyService";
import FormSelectSearchField from "../shared/form-select-search-field";
import moment from "moment";
import FormDateTimePicker from "../shared/form-date-time-picker";
import Button from "../shared/Button";
import PriceField from "../shared/price-field";
import FormTruckLicensePlateField from "../shared/form-truck-license-plate-field";
import { fillBlankTruckFields, isLicensePlateMissing } from "../../utils/ticket-helpers";
import { useTruckNumberAutofill } from "../../hooks";
import { SoilFLOIcon } from "../shared/icons";
import { useSoilFLOStyles } from "../../utils/soilflo-styles";
import { HydrovacScreeningQuestions, HydrovacDistanceOptions, HydrovacYesNoOptions, getScreeningFlags, DefaultSalePrices, DefaultHydrovacScreeningAnswers } from "../../utils/constants";
import { HydrovacReceiveDialogProps } from "../../interface/dialog/HydrovacReceiveDialogProps";

const HydrovacReceiveDialog = ({
  open,
  ticket,
  onClose,
  onReceive,
}: HydrovacReceiveDialogProps) => {
  const sfClasses = useSoilFLOStyles();
  const [editedTicket, setEditedTicket] = useState<HydrovacTicket | null>(null);
  const [contractors, setContractors] = useState<
    { value: number; label: string }[]
  >([]);
  const [formAttempted, setFormAttempted] = useState(false);
  const [containmentPads, setContainmentPads] = useState<ContainmentPad[]>([]);

  const isSoilFLO = editedTicket?.isSoilFLOLoad === true;

  const selectedPad =
    containmentPads.find((p) => p.id === editedTicket?.containmentPadId) ?? null;
  const selectedPadFull = selectedPad
    ? (selectedPad.loadCount ?? 0) >= selectedPad.capacity
    : false;
  const noActivePads = containmentPads.length === 0;

  useEffect(() => {
    if (ticket) {
      // Seed default screening answers for any question the driver left blank;
      // never overwrite answers that already have a value.
      const seededScreening = Object.entries(DefaultHydrovacScreeningAnswers).reduce(
        (acc, [field, def]) => {
          acc[field] = (ticket as any)[field] || def;
          return acc;
        },
        {} as Record<string, string>
      );
      setEditedTicket({
        ...ticket,
        ...seededScreening,
        salePrice: ticket.salePrice ?? DefaultSalePrices.Hydrovac,
        // Default Time Loaded to now (today); only the time is editable.
        timeLoaded: ticket.timeLoaded ?? moment().toDate(),
      });
      setFormAttempted(false);
    }
  }, [ticket]);

  useEffect(() => {
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok && response.data) {
        const data =
          response.data as ContractorCompaniesWithThresholdsResponse;
        const companies = data.contractorCompanies.filter(
          (c) => c.isHydrovac
        );
        setContractors(
          companies.map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
  }, []);

  // Refetch active containment pads when the dialog opens.
  useEffect(() => {
    if (open) {
      storageAreaService.getActiveContainmentPads().then((response) => {
        if (response.ok && response.data) {
          setContainmentPads(response.data as ContainmentPad[]);
        }
      });
    }
  }, [open]);

  // Default to the active pad with the highest load count when none is assigned.
  useEffect(() => {
    if (editedTicket && !editedTicket.containmentPadId && containmentPads.length > 0) {
      setEditedTicket((prev) =>
        prev ? { ...prev, containmentPadId: containmentPads[0].id } : prev
      );
    }
  }, [containmentPads, editedTicket?.id]);

  // Hauler + truck # fills the plate and driver from the registered truck.
  useTruckNumberAutofill(
    editedTicket?.shippingCompany,
    editedTicket?.truckNumber,
    (truck) =>
      setEditedTicket((prev) => {
        if (!prev) return prev;
        const filled = fillBlankTruckFields(truck, {
          licensePlate: prev.truckLicensePlate,
          driverName: prev.shippingDriver,
        });
        return { ...prev, truckLicensePlate: filled.licensePlate, shippingDriver: filled.driverName };
      }),
    !isSoilFLO
  );

  if (!editedTicket) return null;

  const updateField = (field: keyof HydrovacTicket, value: any) => {
    setEditedTicket({ ...editedTicket, [field]: value });
  };

  const isEmpty = (val: any) => val === null || val === undefined || val === "";

  const requiredFields: (keyof HydrovacTicket)[] = isSoilFLO
    ? []
    : ["contractorCompanyId", "shippingDriver"];

  const shippingDriverMissing =
    requiredFields.includes("shippingDriver") && isEmpty(editedTicket.shippingDriver?.trim());

  // SoilFLO loads show the plate read-only, so only our own loads can be blocked on it.
  const licensePlateMissing =
    !isSoilFLO && isLicensePlateMissing(editedTicket.truckLicensePlate);

  const screeningFlags = getScreeningFlags(editedTicket);

  const onSubmit = () => {
    setFormAttempted(true);
    if (noActivePads) {
      toast.error(
        "No active containment pad — create or open one in Storage Areas before receiving."
      );
      return;
    }
    if (isEmpty(editedTicket.containmentPadId)) {
      toast.error("Please select an active containment pad");
      return;
    }
    if (selectedPadFull) {
      toast.error(
        "This containment pad is full. Close it for sampling and select another."
      );
      return;
    }
    const missingFields = requiredFields.some((f) => isEmpty((editedTicket as any)[f]));
    if (missingFields || licensePlateMissing || isEmpty(editedTicket.salePrice)) {
      toast.error("Please fill in all required fields");
      return;
    }
    onReceive({ ...editedTicket, timeUnloaded: null });
  };

  const sfClass = isSoilFLO ? sfClasses.soilFLOField : undefined;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar style={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <IoIosClose />
          </IconButton>
          <Typography style={{ flex: 1 }} variant="h6" component="div">
            Receive Hydrovac Ticket #{hydrovacTicketNumber(editedTicket)}
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" style={{ padding: 24 }}>
        {isSoilFLO && (
          <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
            <SoilFLOIcon />
            <Typography variant="body2" color="textSecondary">
              Fields pulled from SoilFLO are read-only
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
          Source Information
        </Typography>
        <Grid container spacing={3}>
          {/* Row 1: Contractor + Property Address */}
          <Grid item xs={12} sm={6}>
            {isSoilFLO ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Contractor"
                value={editedTicket.contractorCompanyName ?? ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                className={sfClasses.soilFLOField}
              />
            ) : (
              <>
                <FormSelectSearchField
                  name="contractorCompanyId"
                  placeholder="Contractor *"
                  options={contractors}
                  value={
                    editedTicket.contractorCompanyId
                      ? contractors.find(
                          (c) => c.value === editedTicket.contractorCompanyId
                        ) ?? null
                      : null
                  }
                  onChange={(option: any) => {
                    if (option) {
                      setEditedTicket({
                        ...editedTicket,
                        contractorCompanyId: option.value,
                        contractorCompanyName: option.label,
                      });
                    } else {
                      setEditedTicket({
                        ...editedTicket,
                        contractorCompanyId: null,
                        contractorCompanyName: null,
                      });
                    }
                  }}
                  isClearable
                />
                {formAttempted && isEmpty(editedTicket.contractorCompanyId) && (
                  <Typography variant="caption" color="error">Required</Typography>
                )}
              </>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Property Address"
              value={editedTicket.propertyAddress ?? ""}
              onChange={(e) => !isSoilFLO && updateField("propertyAddress", e.target.value)}
              InputProps={{ readOnly: isSoilFLO }}
              InputLabelProps={isSoilFLO ? { shrink: true } : undefined}
              className={sfClass}
            />
          </Grid>

          {/* Row 2: Description of Activity (full width) */}
          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" label="Description of Activity"
              value={editedTicket.descriptionOfActivity ?? ""}
              onChange={(e) => updateField("descriptionOfActivity", e.target.value)}
            />
          </Grid>

          {/* Row 3: Land Use + Work Order + Property Owner */}
          <Grid item xs={12} sm={4}>
            <TextField fullWidth variant="outlined" label="Land Use"
              value={editedTicket.landUse ?? ""}
              onChange={(e) => updateField("landUse", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth variant="outlined" label="Work Order #"
              value={editedTicket.workOrderNumber ?? ""}
              onChange={(e) => updateField("workOrderNumber", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth variant="outlined" label="Property Owner"
              value={editedTicket.propertyOwner ?? ""}
              onChange={(e) => updateField("propertyOwner", e.target.value)}
            />
          </Grid>

          {/* Row 4: Time Loaded + Shipping Company */}
          <Grid item xs={12} sm={6}>
            {isSoilFLO ? (
              <div className={sfClasses.soilFLOField}>
                <FormDateTimePicker
                  label="Time Loaded"
                  value={editedTicket.timeLoaded ?? null}
                  readOnly
                  onChange={undefined}
                />
              </div>
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                type="time"
                label="Time Loaded"
                InputLabelProps={{ shrink: true }}
                value={
                  editedTicket.timeLoaded
                    ? moment(editedTicket.timeLoaded).format("HH:mm")
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value; // "HH:mm"
                  if (!v) {
                    updateField("timeLoaded", null);
                    return;
                  }
                  const [h, m] = v.split(":").map(Number);
                  // Keep the date as today; only the entered time matters.
                  const anchored = moment()
                    .hours(h)
                    .minutes(m)
                    .seconds(0)
                    .milliseconds(0);
                  updateField("timeLoaded", anchored.toISOString());
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth variant="outlined" label="Shipping Company"
              value={editedTicket.shippingCompany ?? ""}
              onChange={(e) => !isSoilFLO && updateField("shippingCompany", e.target.value)}
              InputProps={{ readOnly: isSoilFLO }}
              className={sfClass}
            />
          </Grid>

          {/* Row 5: Shipping Driver + Truck Number + Truck License Plate */}
          <Grid item xs={12} sm={4}>
            <TextField fullWidth variant="outlined" label="Shipping Driver"
              required={!isSoilFLO}
              error={formAttempted && shippingDriverMissing}
              helperText={formAttempted && shippingDriverMissing ? "Required" : ""}
              value={editedTicket.shippingDriver ?? ""}
              onChange={(e) => !isSoilFLO && updateField("shippingDriver", e.target.value)}
              InputProps={{ readOnly: isSoilFLO }}
              className={sfClass}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth variant="outlined" label="Truck Number"
              value={editedTicket.truckNumber ?? ""}
              onChange={(e) => !isSoilFLO && updateField("truckNumber", e.target.value)}
              InputProps={{ readOnly: isSoilFLO }}
              className={sfClass}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            {isSoilFLO ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Truck License Plate"
                value={editedTicket.truckLicensePlate?.toUpperCase() ?? ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                className={sfClasses.soilFLOField}
              />
            ) : (
              <>
                <FormTruckLicensePlateField
                  value={editedTicket.truckLicensePlate ?? ""}
                  name="truckLicensePlate"
                  label="Truck License Plate *"
                  onChange={(e) =>
                    updateField("truckLicensePlate", e.target.value.toUpperCase())
                  }
                />
                {formAttempted && licensePlateMissing && (
                  <Typography variant="caption" color="error">Required</Typography>
                )}
              </>
            )}
          </Grid>
        </Grid>

        {/* USM Receiving */}
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 24, marginBottom: 12 }}>
          USM Receiving
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              variant="outlined"
              required
              error={
                (formAttempted && isEmpty(editedTicket.containmentPadId)) ||
                selectedPadFull ||
                noActivePads
              }
            >
              <InputLabel>Active Containment Pad</InputLabel>
              <Select
                label="Active Containment Pad"
                value={editedTicket.containmentPadId ?? ""}
                onChange={(e) => updateField("containmentPadId", e.target.value)}
              >
                {containmentPads.map((p) => {
                  const full = (p.loadCount ?? 0) >= p.capacity;
                  return (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} — {p.loadCount ?? 0} / {p.capacity}
                      {full ? " (FULL)" : ""}
                    </MenuItem>
                  );
                })}
              </Select>
              {noActivePads && (
                <Typography variant="caption" color="error">
                  No active containment pad — create or open one in Storage Areas.
                </Typography>
              )}
              {selectedPadFull && (
                <Typography variant="caption" color="error">
                  This containment pad is full. Close it for sampling and select
                  another.
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <PriceField
              label="Sale Price *"
              value={editedTicket.salePrice}
              onChange={(val) => updateField("salePrice", val)}
            />
            {formAttempted && isEmpty(editedTicket.salePrice) && (
              <Typography variant="caption" color="error">Required</Typography>
            )}
          </Grid>
        </Grid>

        <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 24, marginBottom: 12 }}>
          Screening Questions
        </Typography>
        <Grid container spacing={3}>
              {HydrovacScreeningQuestions.map(({ field, label, type }) => {
                const options = type === "distance" ? HydrovacDistanceOptions : HydrovacYesNoOptions;
                return (
                  <Grid item xs={12} sm={4} key={field}>
                    <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 500 }}>
                      {label}
                    </Typography>
                    <TextField select fullWidth variant="outlined"
                      value={(editedTicket as any)[field] ?? ""}
                      onChange={(e) => updateField(field as keyof HydrovacTicket, e.target.value)}
                    >
                      <MenuItem value=""><em>Select...</em></MenuItem>
                      {options.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                );
              })}
        </Grid>

        {screeningFlags.length > 0 && (
          <Box style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: "#fff3e0",
            border: "1px solid #ef6c00",
            borderRadius: 4,
          }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#e65100", marginBottom: 4 }}>
              Screening Flags — Please double-check and update the following questions:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {screeningFlags.map((flag) => (
                <li key={flag}>
                  <Typography variant="body2" style={{ color: "#bf360c" }}>{flag}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}

        <Box style={{ display: "flex", justifyContent: "flex-end", paddingTop: 8, marginTop: 12 }}>
          <div style={{ width: 180, maxWidth: "100%" }}>
            <Button
              label="Receive Ticket"
              onClick={onSubmit}
              color="primary"
              variant="contained"
              type="button"
            />
          </div>
        </Box>
      </Container>
    </Dialog>
  );
};

export default HydrovacReceiveDialog;
